/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";

import { Button, Container, Placeholder } from "semantic-ui-react";

import useCurrentUser from "data/advisor/useCurrentUser";
import useHub from "data/hub/useHub";
import { CreateSync, GetSync } from "data/libs/sync";

import { SetAlert } from "components/Alerts/actions";
import { useFeatures } from "components/FeatureFlag";
import InfiniteScroll from "components/InfiniteScroll";

import useCommentFilters from "./CommentFilter/hooks";
import useClientComments from "../hooks/useClientComments";
import CardComment from "./CardComment";
import CommentListHeader from "./CommentListHeader";

function EmptyComment() {
  return (
    <Container style={{ padding: "3em 0em" }} data-test="loading-comments">
      <Placeholder fluid>
        <Placeholder.Header image>
          <Placeholder.Line />
          <Placeholder.Line />
        </Placeholder.Header>
        <Placeholder.Paragraph>
          <Placeholder.Line />
          <Placeholder.Line />
          <Placeholder.Line />
        </Placeholder.Paragraph>
      </Placeholder>
    </Container>
  );
}

function CardCommentsList({ client }) {
  const { workflowId: clientWorkflowId } = useParams();
  const ff = useFeatures("merge-client-card");
  const dispatch = useDispatch();
  const advisor = useCurrentUser();
  const hub = useHub();
  const clientWorkflow = useSelector((state) => {
    return state.hubly.data.hub.clientWorkflows[clientWorkflowId];
  });
  const {
    filterByTaskUuid,
    activeFilters,
    ignoreCommentGid,
    filterByClientWorkflowUuid,
  } = useCommentFilters(clientWorkflowId);

  const { comments, initialFetch } = useClientComments(
    client.id,
    ignoreCommentGid,
    filterByTaskUuid,
    filterByClientWorkflowUuid
  );

  const checkSyncComplete = (syncId) => {
    GetSync(syncId).then((response) => {
      if (response.status === "failed") {
        if (!client.isDeletedInCrm)
          dispatch(
            SetAlert({ type: "error", text: "Failed to sync comments." })
          );
      } else if (response.status === "completed") {
        comments.refetch();
      } else {
        setTimeout(() => {
          checkSyncComplete(syncId);
        }, 1000);
      }
    });
  };

  const createSync = () => {
    const request = {
      type: "comments",
      advisor_id: advisor.id,
      client_id: client.id,
      hub_id: hub.id,
    };

    if (
      advisor.integrations.find((i) => {
        return i.hubId === hub.id;
      })
    ) {
      CreateSync(request).then((response) => {
        const syncId = response.id;
        setTimeout(() => {
          checkSyncComplete(syncId);
        }, 10);
      });
    }
  };

  useEffect(() => {
    createSync();
  }, []);

  if (initialFetch) {
    return (
      <div style={{ marginTop: "2em", marginLeft: "0em" }}>
        <CommentListHeader
          activeFilters={activeFilters}
          client={client}
          hub={hub}
          disabled={Boolean(client.isDeletedInCrm && ff?.mergeOperation)}
          refetchComments={comments.refetch}
        />
        <EmptyComment />
      </div>
    );
  }

  const disabled = Boolean(client.isDeletedInCrm && ff?.mergeOperation);

  return (
    <div
      data-test="client-card-comments"
      style={{ marginTop: "1em", marginLeft: "0em" }}
    >
      <CommentListHeader
        activeFilters={activeFilters}
        client={client}
        hub={hub}
        disabled={Boolean(client.isDeletedInCrm && ff?.mergeOperation)}
        refetchComments={comments.refetch}
      />
      {comments.data.length === 0 ? (
        <div
          style={{ color: "grey", marginLeft: "3em" }}
          data-test="no-comments-message"
        >
          {`${
            client.firstName || client.lastName || "The client"
          } has no comments`}
          {activeFilters.length > 0
            ? " matching currently selected filter."
            : "."}
        </div>
      ) : (
        <React.Fragment>
          <Container
            style={{ padding: "1em 0em 1em 3em" }}
            className="comment_list"
          >
            <InfiniteScroll
              loadingPage={comments.loading}
              hasMorePages={comments.hasMorePages}
              onLoadMore={comments.readNext}
              style={{ display: "flex", flexDirection: "column" }}
              pullNextPageElement={
                <Button
                  data-test="load-more-comments-button"
                  size="tiny"
                  basic
                  onClick={comments.readNext}
                  style={{
                    textAlign: "center",
                    alignSelf: "center",
                    marginTop: "1em",
                    marginBottom: "1em",
                  }}
                >
                  Load More
                </Button>
              }
              loadingElement={<EmptyComment />}
            >
              {comments.data.map((comment) => {
                const key = `${comment.uuid}_${comment.createdAt}`;
                const commentTask = clientWorkflow?.tasks.find((t) => {
                  return (
                    t.id === comment.taskUuid ||
                    (t.comments?.length > 0 &&
                      t.comments.some((tc) => {
                        return tc.id === comment.uuid;
                      }))
                  );
                });
                const commentClientWorkflow =
                  commentTask ||
                  comment.clientWorkflowUuid === clientWorkflow?.id
                    ? clientWorkflow
                    : undefined;

                return (
                  <CardComment
                    onEditClientComment={comments.refetch}
                    onDeleteClientComment={comments.refetch}
                    externalId={client.externalId}
                    comment={comment}
                    disabled={disabled}
                    key={key}
                    clientWorkflow={commentClientWorkflow}
                    task={commentTask}
                    client={client}
                  />
                );
              })}
            </InfiniteScroll>
          </Container>
        </React.Fragment>
      )}
    </div>
  );
}

CardCommentsList.propTypes = {
  client: PropTypes.shape({
    externalId: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    clientPoolId: PropTypes.string.isRequired,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    comments: PropTypes.array,
    isDeletedInCrm: PropTypes.bool,
  }).isRequired,
};

export default CardCommentsList;
